// Alignment spec helpers. Specs are now driven by the selected vehicle (see VehicleContext).

export const EMPTY_MEASUREMENTS = {
  frontLeft: { camber: "", caster: "" },
  frontRight: { camber: "", caster: "" },
  rearLeft: { camber: "" },
  rearRight: { camber: "" },
  frontToe: "",
  rearToe: "",
};

// Compact fallback vehicle (used before the catalog loads).
export const DEFAULT_VEHICLE = {
  id: "hummer-h2", make: "Hummer", model: "H2", year: 2006, years: [2003, 2009], drivetrain: "4WD",
  front: { camber: [0.0, -0.5, 0.5], caster: [3.0, 2.5, 3.5], toe: [0.10, 0.05, 0.15] },
  rear: { camber: [0.0, -0.5, 0.5], toe: [0.0, -0.10, 0.10], adjustable: false },
  rideHeight: { zHeightFront: "72.5 mm ± 6 mm", dHeightRear: "115 mm ± 6 mm",
                notes: "Verify Z/D ride height and jounce the suspension 3× before measuring." },
  torque: { camBoltNut: "190 N·m (140 lb-ft)", tieRodJamNut: "68 N·m (50 lb-ft)" },
};

// Expand a compact catalog record into the full specs object the UI consumes.
export function buildSpecs(v) {
  if (!v) return buildSpecs(DEFAULT_VEHICLE);
  const mk = (arr, adjustable, method) => ({ target: arr[0], min: arr[1], max: arr[2], unit: "deg", adjustable, method });
  const label = `${v.year ?? ""} ${v.make} ${v.model}`.trim();
  return {
    vehicle: label.toUpperCase(),
    make: v.make, model: v.model, year: v.year, drivetrain: v.drivetrain,
    front: {
      camber: mk(v.front.camber, true, "Front camber — per service manual"),
      caster: mk(v.front.caster, true, "Front caster — per service manual"),
      totalToe: mk(v.front.toe, true, "Front toe — tie rod adjustment"),
      crossMaxDiff: 0.5,
    },
    rear: {
      camber: mk(v.rear.camber, v.rear.adjustable ?? false, v.rear.adjustable ? "Rear camber — adjustable" : "Rear camber — check per service manual"),
      totalToe: mk(v.rear.toe, v.rear.adjustable ?? false, v.rear.adjustable ? "Rear toe — adjustable" : "Rear toe — check per service manual"),
    },
    rideHeight: v.rideHeight || null,
    torque: v.torque || null,
  };
}

export const DEFAULT_SPECS = buildSpecs(DEFAULT_VEHICLE);
// Backward-compatible alias.
export const H2_SPECS = DEFAULT_SPECS;

// Suspension lift/leveling presets. Offsets shift the acceptable front camber/caster
// window because raising the ride height rotates the knuckle/axle (est. guidance).
export const LIFT_PRESETS = {
  stock: { label: "Stock", camber: 0, caster: 0 },
  level: { label: "Leveling Kit", camber: -0.2, caster: -0.3 },
  lift2: { label: '2" Lift', camber: -0.3, caster: -0.8 },
  lift4: { label: '4"+ Lift', camber: -0.5, caster: -1.5 },
};

export function applyLift(specs, liftKey) {
  if (!specs || !liftKey || liftKey === "stock") return specs ? { ...specs, liftKey: "stock" } : specs;
  const p = LIFT_PRESETS[liftKey];
  if (!p) return specs;
  const shift = (spec, d) => ({ ...spec, target: +(spec.target + d).toFixed(2), min: +(spec.min + d).toFixed(2), max: +(spec.max + d).toFixed(2) });
  return {
    ...specs,
    liftKey,
    front: {
      ...specs.front,
      camber: shift(specs.front.camber, p.camber),
      caster: shift(specs.front.caster, p.caster),
    },
  };
}

const toNum = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

function rateValue(value, spec) {
  const v = toNum(value);
  if (v === null || Number.isNaN(v)) return "empty";
  if (v < spec.min || v > spec.max) return "fail";
  const span = spec.max - spec.min;
  const edge = span * 0.15;
  if (v < spec.min + edge || v > spec.max - edge) return "warn";
  return "pass";
}

export function evaluate(m, specs = DEFAULT_SPECS) {
  const checks = [
    { key: "fl-camber", axle: "front", label: "Front Left Camber", value: m.frontLeft?.camber, spec: specs.front.camber },
    { key: "fr-camber", axle: "front", label: "Front Right Camber", value: m.frontRight?.camber, spec: specs.front.camber },
    { key: "fl-caster", axle: "front", label: "Front Left Caster", value: m.frontLeft?.caster, spec: specs.front.caster },
    { key: "fr-caster", axle: "front", label: "Front Right Caster", value: m.frontRight?.caster, spec: specs.front.caster },
    { key: "front-toe", axle: "front", label: "Front Total Toe", value: m.frontToe, spec: specs.front.totalToe },
    { key: "rl-camber", axle: "rear", label: "Rear Left Camber", value: m.rearLeft?.camber, spec: specs.rear.camber },
    { key: "rr-camber", axle: "rear", label: "Rear Right Camber", value: m.rearRight?.camber, spec: specs.rear.camber },
    { key: "rear-toe", axle: "rear", label: "Rear Total Toe", value: m.rearToe, spec: specs.rear.totalToe },
  ].map((c) => ({ ...c, status: rateValue(c.value, c.spec) }));

  const cross = [];
  const flc = toNum(m.frontLeft?.camber), frc = toNum(m.frontRight?.camber);
  if (flc !== null && frc !== null) {
    const diff = Math.abs(flc - frc);
    cross.push({ key: "cross-camber", label: "Cross Camber (L-R)", diff, max: specs.front.crossMaxDiff, status: diff > specs.front.crossMaxDiff ? "fail" : "pass" });
  }
  const fls = toNum(m.frontLeft?.caster), frs = toNum(m.frontRight?.caster);
  if (fls !== null && frs !== null) {
    const diff = Math.abs(fls - frs);
    cross.push({ key: "cross-caster", label: "Cross Caster (L-R)", diff, max: specs.front.crossMaxDiff, status: diff > specs.front.crossMaxDiff ? "fail" : "pass" });
  }

  const filled = checks.filter((c) => c.status !== "empty");
  const passing = filled.filter((c) => c.status === "pass" || c.status === "warn").length;
  const passRate = filled.length ? Math.round((passing / filled.length) * 100) : 0;
  const hasFail = filled.some((c) => c.status === "fail") || cross.some((c) => c.status === "fail");

  return { checks, cross, filled: filled.length, total: checks.length, passing, passRate, hasFail };
}

export const STATUS_META = {
  pass: { label: "IN SPEC", color: "#10B981", bg: "rgba(16,185,129,0.15)" },
  warn: { label: "MARGINAL", color: "#F59E0B", bg: "rgba(245,158,11,0.15)" },
  fail: { label: "OUT OF SPEC", color: "#EF4444", bg: "rgba(239,68,68,0.15)" },
  empty: { label: "—", color: "#64748B", bg: "rgba(100,116,139,0.1)" },
};
